import initApiServices from '@/services/ApiInitializer';

const DEFAULT_MIMETYPES = ['image/*', 'video/mp4'];
function cleanSASUrl(sasUrl) {
    return sasUrl.split('?')[0];
}
function fetchErroHandler(err) {
    console.log(err);
    return {
        data: {
            total: 0,
            assets: []
        }
    };
}
function imgToThumb(image) {
    let thumbURL = image.url;
    const imageType = 'tribute-photos';

    const imgPath = thumbURL.split(`/${imageType}/`)[1];
    
    // Video Type Thumbnails require that the transforms be at the end e.g: `?so=`

    if (thumbURL.toLowerCase().includes('.mp4')) {
        thumbURL = `${process.env.VUE_APP_IMG_KIT_BASE}${imgPath}/ik-thumbnail.jpg?tr=${['w-200', 'so-2'].join()}`;

        return thumbURL;
    }

    return `${process.env.VUE_APP_IMG_KIT_BASE}${imgPath ? imgPath + '/' : ''}tr:${['w-200', 'fo-auto'].join()
        }/ik-thumbnail.jpg`;
}
function translateToAssetResult(image) {

    const thumbUri = imgToThumb(image);
    const id = image.externalId.toString();
    let asset = {
        id,
        locale: 'en',
        meta: {
            width: image?.width || 250,
            height: image?.height || 250,
            thumbUri,
            mimeType: thumbUri.toLowerCase().includes('.mp4') ? 'video/mp4' : 'image/jpeg',
            uri: image.url
        },
        context: {
            sourceId: 'mst'
        },
        // credits
    };
    if (image.height) {
        asset.meta.height = image.height;
    }
    if (image.width) {
        asset.meta.width = image.width;
    }
    return asset;
}

export const funeralHomeTributeAssetFactory = (token, options = {}) => {
    if (!token) {
        throw new Error('Error initializing Asset Factory, no token provided');
    }

    let tributeAssetFactory = {
        id: 'memshare-funeralhome-assets',
        title: 'Funeral Home Assets',
        api: initApiServices(token),
        // Only admins should be able to add and remove media
        eventId: options.eventId,
        instance: options.instance,
        funeralHomeId: options.funeralHomeId,
        getSupportedMimeTypes() {
            return options?.supportedMimeTypes || DEFAULT_MIMETYPES;
        },
        async findAssets(queryData) {
            console.log('funeralHomeTributeAssetFactory findAssets called', queryData);
            const page = queryData.page > 0 ? queryData.page : undefined;
            let payload = {
                pageNumber: page,
                pageSize: queryData.perPage,
                reversed: true,
            };
            const { data } = await this.api.tributePhoto.getFuneralHomeAssets(this.funeralHomeId, payload)
                .catch(fetchErroHandler);
            return formatResponse(data, queryData);
        },
        /**
         * Payload description for a file being added through imgly CESDK
         * @param {Object} file - File object to upload
         * @param {string} file.id - File object to upload
         * @param {Array} file.groups - Describes the `groups` field
         * @param {Object} file.payload - Describes the `payload` field
         * @param {Array} file.payload.sourceSet - Describes the `payload` field
         * @param {string} file.payload.sourceSet[].uri - URI for this file the file
         * @param {number} file.payload.sourceSet[].height - height of asset
         * @param {number} file.payload.sourceSet[].width - width of asset
         * @param {Objet} file.tags - Describes the `tags` field
         * @param {Object} file.credits - Describes the `credits` field
         * @param {Object} file.license - Describes the `license` field
         * @param {Object} file.utm - ,Describes the `utm` field
         * @param {Object} file.meta - Describes the `meta` field
         * @param {string} file.meta.filename - filename
         * @param {string} file.meta.mimeType - mimeType
         * @param {string} file.meta.width - width
         * @param {string} file.meta.previewUri - blob uri
         * @param {string} file.meta.height - height
         * @param {string} file.meta.thumbUri - blob uri
         * @param {string} file.meta.uri - blob uri
         */
        async addAsset(file) {
            // console.log('add Asset called from tributeAssetFactory', { file }, this.api);
            const { filename, uri, mimeType, height, width } = file.meta;
            if (!DEFAULT_MIMETYPES.some((a) => new RegExp(a).test(mimeType))) {
                // const err = new Error('Unsupported file format');
                // console.error(err);
                this.instance.ui.showNotification({
                    type: 'error', message: 'Upload failed, unsupported file format.'
                });
                return false;
            }
            const { data: SAS_INFO } = await this.api.tributePhoto
                .getUploadUrlForFuneralHomeAsset(this.funeralHomeId, filename);
            const blob = await fetch(uri).then(r => r.blob());
            const uploadObject = new File([blob], SAS_INFO.fileName);
            return new Promise((resolve, reject) => {
                this.api.blob.upload(SAS_INFO.sas, uploadObject)
                    .then(() => {
                        return this.api.tributePhoto.postFuneralHomeAsset(this.funeralHomeId, [{
                            url: cleanSASUrl(SAS_INFO.sas),
                            name: SAS_INFO.fileName,
                            height,
                            width
                        }])
                        .then(uploadResult => {
                            console.log('newly added asset', uploadResult);
                            const { data: newAssets } = uploadResult;
                            resolve(newAssets.map(image => translateToAssetResult(image)));
                            this.instance.engine.asset.assetSourceContentsChanged(this.id);
                        });
                    })
                    .catch(err => {
                        console.error(err);
                        reject('There was an issue while uploading your media');
                    });
            });
        },
        async removeAsset(assetId) {
            const { data, status } = await this.api.tributePhoto.deleteEditorAssetByID(assetId)
                .catch((err) => {
                    return {
                        status: 400,
                        data: null
                    }
                });
            if (status === 200) {
                this.instance.engine.asset.assetSourceContentsChanged(this.id);
                return true;
            } else {
                const error = new Error(`Error while trying to delete media`);
                console.error(error);
                return false;
            }
        },
        entryOptions: {
            canAdd: options?.canAdd || false,
            canRemove: options?.canRemove || false,
        }
    }
    return tributeAssetFactory;
}
// Asset factory for Tibute Video
export const tributeAssetFactory = (token, options = {}) => {
    if (!token) {
        throw new Error('Error initializing Tribute Asset Factory, no token provided');
    }
    // should be tied to an event-specific bucket
    if (!options.eventId) {
        throw new Error('Error initializing Tribute Asset Factory, "eventId" not provided');
    }

    let tributeAssetFactory = {
        id: 'memshare-tribute-assets',
        title: 'Tribute Assets',
        api: initApiServices(token),
        eventId: options.eventId,
        funeralHomeId: options.funeralHomeId,
        instance: options.instance,
        getSupportedMimeTypes() {
            return options?.supportedMimeTypes || DEFAULT_MIMETYPES;
        },
        async findAssets(queryData) {
            console.log('funeralHomeTributeAssetFactory findAssets called', queryData);
            // TODO: Use Scene Mode in order to filter videos from showing up for design scenes
            // because it will show only the first frame of a video when places in a design scene
            // const sceneMode = options.instance.engine.scene.getMode();
            
            const page = queryData.page > 0 ? queryData.page : undefined;
            let payload = {
                pageNumber: page,
                pageSize: queryData.perPage,
                reversed: true,
            };
            const { data, status } = await this.api.tributePhoto.getTributeEventEditorAssets(this.eventId, payload)
                .catch(fetchErroHandler);
            return formatResponse(data, queryData);
        },
        /**
         * Payload description for a file being added through imgly CESDK
         * @param {Object} file - File object to upload
         * @param {string} file.id - File object to upload
         * @param {Array} file.groups - Describes the `groups` field
         * @param {Object} file.payload - Describes the `payload` field
         * @param {Array} file.payload.sourceSet - Describes the `payload` field
         * @param {string} file.payload.sourceSet[].uri - URI for this file the file
         * @param {number} file.payload.sourceSet[].height - height of asset
         * @param {number} file.payload.sourceSet[].width - width of asset
         * @param {Objet} file.tags - Describes the `tags` field
         * @param {Object} file.credits - Describes the `credits` field
         * @param {Object} file.license - Describes the `license` field
         * @param {Object} file.utm - ,Describes the `utm` field
         * @param {Object} file.meta - Describes the `meta` field
         * @param {string} file.meta.filename - filename
         * @param {string} file.meta.mimeType - mimeType
         * @param {string} file.meta.width - width
         * @param {string} file.meta.previewUri - blob uri
         * @param {string} file.meta.height - height
         * @param {string} file.meta.thumbUri - blob uri
         * @param {string} file.meta.uri - blob uri
         */
        async addAsset(file) {
            // console.log('add Asset called from tributeAssetFactory', { file }, this.api);
            const { filename, uri, mimeType, height, width } = file.meta;
            if (!DEFAULT_MIMETYPES.some((type) => new RegExp(type).test(mimeType))) {
                this.instance.ui.showNotification({
                    type: 'error', message: 'Upload failed, unsupported file format.'
                });
                return false;
            }
            const { data: SAS_INFO } = await this.api.tributePhoto
                .getUploadUrlForTributeEventEditorAsset(this.eventId, filename);
            const blob = await fetch(uri).then(r => r.blob());
            const uploadObject = new File([blob], SAS_INFO.fileName);
            return new Promise((resolve, reject) => {
                this.api.blob.upload(SAS_INFO.sas, uploadObject)
                    .then(() => {
                        return this.api.tributePhoto.postTributeEventEditorAsset(this.eventId, [{
                            url: cleanSASUrl(SAS_INFO.sas),
                            name: SAS_INFO.fileName,
                            height,
                            width
                        }])
                        .then(uploadResult => {
                            // console.log('newly added asset', uploadResult);
                            const { data: newAssets } = uploadResult;
                            resolve(newAssets.map(image => translateToAssetResult(image)));
                            this.instance.engine.asset.assetSourceContentsChanged(this.id);
                        });
                    })
                    .catch(err => {
                        console.error(err);
                        reject('There was an issue while uploading your media');
                    });
            });
        },
        async removeAsset(assetId) {
            const { status } = await this.api.tributePhoto.deleteEditorAssetByID(assetId)
                .catch((err) => {
                    return {
                        status: 400,
                        data: null
                    }
                });
            if (status === 200) {
                this.instance.engine.asset.assetSourceContentsChanged(this.id);
                return true;
            } else {
                const error = new Error(`Error while trying to delete media`);
                console.error(error);
                return false;
            }
        },
        // TODO: update based on permissions
        entryOptions: {
            canAdd: options?.canAdd || false,
            canRemove: options?.canRemove || false,
        }
    }

    return tributeAssetFactory;
}

function formatResponse(response = {}, queryData) {
    const total = response?.total || 0;
    const results = response?.assets || [];
    const assets = results.map((image) => translateToAssetResult(image));
    const nextPage = results.length > 0 ? (queryData.page ?? 0) + 1 : undefined;
    const formattedResponse = {
        assets,
        total,
        nextPage,
        currentPage: queryData.page ?? 0
    };
    return formattedResponse;
}